import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Card, 
  Button, 
  message,
  Space,
  Tag,
  Empty,
  Spin,
  Select,
  InputNumber,
  Statistic,
  Row,
  Col,
  Tooltip,
  Alert,
  Radio,
  Modal
} from 'antd';
import { 
  ReloadOutlined,
  FilterOutlined,
  NodeIndexOutlined,
  ClearOutlined,
  DatabaseOutlined,
  BranchesOutlined,
  ApartmentOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { getOntologyMermaid, resetOntologyCache } from '../services/dataService';
import MermaidViewer from '../components/MermaidViewer';

const { Title, Paragraph, Text } = Typography;

function Ontology() {
  const [mermaidCode, setMermaidCode] = useState('');
  const [originalMermaidCode, setOriginalMermaidCode] = useState('');
  const [filteredMermaidCode, setFilteredMermaidCode] = useState('');
  const [allViews, setAllViews] = useState([]);
  const [viewLabels, setViewLabels] = useState({});
  const [viewAccessDenied, setViewAccessDenied] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedViews, setSelectedViews] = useState([]);
  const [relationshipLevels, setRelationshipLevels] = useState(2);
  const [searchMode, setSearchMode] = useState('connected_paths');
  const [ontologySource, setOntologySource] = useState(null);
  const [stats, setStats] = useState({
    viewCount: 0,
    relationshipCount: 0,
    filteredViewCount: 0,
    filteredRelationshipCount: 0
  });

  useEffect(() => {
    loadMermaidDiagram();
  }, []);

  const loadMermaidDiagram = async () => {
    setLoading(true);
    try {
      const data = await getOntologyMermaid();
      if (data.empty) {
        // No ontology file — show empty state, not an error
        setMermaidCode('');
        setOriginalMermaidCode('');
        setFilteredMermaidCode('');
        setAllViews([]);
        setViewLabels({});
        setStats({ viewCount: 0, relationshipCount: 0, filteredViewCount: 0, filteredRelationshipCount: 0 });
        return;
      }
      setMermaidCode(data.mermaid_code);
      setOriginalMermaidCode(data.mermaid_code);
      setFilteredMermaidCode(data.mermaid_code);
      setAllViews(data.views || []);
      setViewLabels(data.view_labels || {});
      setViewAccessDenied(data.view_access_denied || {});
      setStats({
        viewCount: data.view_count || 0,
        relationshipCount: data.relationship_count || 0,
        filteredViewCount: data.view_count || 0,
        filteredRelationshipCount: data.relationship_count || 0
      });
    } catch (error) {
      message.error('Failed to load ontology diagram');
      console.error('Error loading ontology:', error);
    } finally {
      setLoading(false);
    }
  };

  // Parse mermaid graph to extract nodes and relationships
  const parseMermaidGraph = (code) => {
    const nodes = new Set();
    const relationships = [];
    
    const lines = code.split('\n');
    for (const line of lines) {
      const trimmed = line.trim();
      
      // Skip graph TD declaration and empty lines
      if (!trimmed || trimmed.startsWith('graph ')) continue;
      
      // Extract node definitions: NODE["Label"]
      if (trimmed.includes('[') && trimmed.includes(']') && !trimmed.includes('-->')) {
        const nodeMatch = trimmed.split('[')[0].trim();
        if (nodeMatch) nodes.add(nodeMatch);
      }
      
      // Extract relationships: NODE1 -->|label| NODE2
      if (trimmed.includes('-->')) {
        const parts = trimmed.split('-->');
        if (parts.length === 2) {
          let source = parts[0].trim();
          let targetPart = parts[1].trim();
          
          // Handle labeled relationships: |"label"| TARGET
          if (targetPart.startsWith('|')) {
            const pipeIndex = targetPart.indexOf('|', 1);
            if (pipeIndex > 0) {
              targetPart = targetPart.substring(pipeIndex + 1).trim();
            }
          }
          
          // Clean up node names
          if (source.includes('[')) source = source.split('[')[0].trim();
          if (targetPart.includes('[')) targetPart = targetPart.split('[')[0].trim();
          
          if (source && targetPart) {
            nodes.add(source);
            nodes.add(targetPart);
            relationships.push([source, targetPart]);
          }
        }
      }
    }
    
    return { nodes: Array.from(nodes), relationships };
  };

  // Get connected views within specified levels (Way 1: Relationship Levels)
  const getConnectedViews = (startViews, relationships, levels) => {
    const connected = new Set(startViews);
    let currentLevel = new Set(startViews);
    
    for (let i = 0; i < levels; i++) {
      const nextLevel = new Set();
      
      for (const node of currentLevel) {
        for (const [source, target] of relationships) {
          if (source === node) nextLevel.add(target);
          if (target === node) nextLevel.add(source);
        }
      }
      
      nextLevel.forEach(n => connected.add(n));
      currentLevel = nextLevel;
      
      if (currentLevel.size === 0) break;
    }
    
    return connected;
  };

  // Get views on ALL shortest connecting paths between selected views (Way 2: Connected Paths)
  const getConnectedPathViews = (startViews, relationships) => {
    // Build undirected adjacency list
    const adj = {};
    for (const [source, target] of relationships) {
      if (!adj[source]) adj[source] = new Set();
      if (!adj[target]) adj[target] = new Set();
      adj[source].add(target);
      adj[target].add(source);
    }

    const result = new Set(startViews);

    // For each pair of selected views, find ALL shortest paths using BFS + predecessor tracking
    for (let i = 0; i < startViews.length; i++) {
      for (let j = i + 1; j < startViews.length; j++) {
        const start = startViews[i];
        const end = startViews[j];

        if (!adj[start] || !adj[end]) continue;

        // BFS with predecessor tracking — records every predecessor that leads to
        // a node via a shortest path, enabling full enumeration of all shortest paths.
        const dist = { [start]: 0 };
        const preds = {}; // preds[node] = list of nodes that precede it on shortest paths
        const queue = [start];

        while (queue.length > 0) {
          const node = queue.shift();
          for (const neighbor of (adj[node] || [])) {
            if (dist[neighbor] === undefined) {
              dist[neighbor] = dist[node] + 1;
              preds[neighbor] = [node];
              queue.push(neighbor);
            } else if (dist[neighbor] === dist[node] + 1) {
              // Another equally-short path reaches this neighbor — record all predecessors
              preds[neighbor].push(node);
            }
          }
        }

        if (dist[end] === undefined) continue; // No path found

        // Backtrack from end → start via predecessors, collecting every node on every shortest path
        const pathNodes = new Set([end]);
        let frontier = [end];
        while (frontier.length > 0) {
          const next = [];
          for (const node of frontier) {
            if (node === start) continue;
            for (const pred of (preds[node] || [])) {
              if (!pathNodes.has(pred)) {
                pathNodes.add(pred);
                next.push(pred);
              }
            }
          }
          frontier = next;
        }

        pathNodes.forEach(n => result.add(n));
      }
    }

    return result;
  };

  // Filter mermaid graph based on selected views
  const filterMermaidGraph = (code, selectedViews, levels, mode) => {
    if (!selectedViews || selectedViews.length === 0) {
      return code;
    }
    
    const { nodes, relationships } = parseMermaidGraph(code);
    const viewsToShow = mode === 'connected_paths'
      ? getConnectedPathViews(selectedViews, relationships)
      : getConnectedViews(selectedViews, relationships, levels);
    
    // Rebuild mermaid code with only filtered nodes and relationships
    const filteredLines = ['graph TD'];
    const originalLines = code.split('\n');
    
    for (const line of originalLines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('graph ')) continue;
      
      // Check if this line defines a node we want to keep
      if (trimmed.includes('[') && trimmed.includes(']') && !trimmed.includes('-->')) {
        const nodeId = trimmed.split('[')[0].trim();
        if (viewsToShow.has(nodeId)) {
          filteredLines.push(`    ${trimmed}`);
        }
      }
      
      // Check if this is a relationship we want to keep
      else if (trimmed.includes('-->')) {
        const parts = trimmed.split('-->');
        if (parts.length === 2) {
          let source = parts[0].trim();
          let targetPart = parts[1].trim();
          
          // Remove the label part if it exists
          if (targetPart.startsWith('|')) {
            const closingPipeIdx = targetPart.indexOf('|', 1);
            if (closingPipeIdx > 0) {
              targetPart = targetPart.substring(closingPipeIdx + 1).trim();
            }
          }
          
          // Remove any node definition suffix
          if (targetPart.includes('[')) {
            targetPart = targetPart.split('[')[0].trim();
          }
          
          // Check if both source and target should be shown
          if (viewsToShow.has(source) && viewsToShow.has(targetPart)) {
            filteredLines.push(`    ${trimmed}`);
          }
        }
      }
    }
    
    return filteredLines.join('\n');
  };

  const handleApplyFilter = () => {
    if (!selectedViews || selectedViews.length === 0) {
      message.warning('Please select at least one view to filter');
      return;
    }
    
    const filtered = filterMermaidGraph(originalMermaidCode, selectedViews, relationshipLevels, searchMode);
    setFilteredMermaidCode(filtered);
    
    // Update stats
    const { nodes: filteredNodes, relationships: filteredRels } = parseMermaidGraph(filtered);
    setStats(prev => ({
      ...prev,
      filteredViewCount: filteredNodes.length,
      filteredRelationshipCount: filteredRels.length
    }));
    
    const modeLabel = searchMode === 'connected_paths' ? 'Connected Paths' : `${relationshipLevels} level(s)`;
    message.success(`Filtered to ${filteredNodes.length} views using ${modeLabel}`);
  };

  const handleResetFilter = () => {
    setSelectedViews([]);
    setRelationshipLevels(2);
    setSearchMode('connected_paths');
    setFilteredMermaidCode(originalMermaidCode);
    
    setStats(prev => ({
      ...prev,
      filteredViewCount: prev.viewCount,
      filteredRelationshipCount: prev.relationshipCount
    }));
    
    message.success('Filter reset - showing all views');
  };

  // Manual ontology generation has been removed. The ontology is now
  // regenerated automatically by the data-service at the end of every
  // catalog sync (manual sync from Data Explorer or scheduled sync).
  // The Reset button below still lets admins clear the generated file
  // if they want to force a clean rebuild on the next catalog sync.
  const handleResetOntology = () => {
    Modal.confirm({
      title: 'Reset Ontology Cache',
      content: 'This will delete the generated ontology diagram file. You can regenerate it from the catalog afterwards. Continue?',
      okText: 'Reset Ontology',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await resetOntologyCache();
          setMermaidCode('');
          setOriginalMermaidCode('');
          setFilteredMermaidCode('');
          setAllViews([]);
          setViewLabels({});
          setSelectedViews([]);
          setOntologySource(null);
          setStats({ viewCount: 0, relationshipCount: 0, filteredViewCount: 0, filteredRelationshipCount: 0 });
          message.success('Ontology cache has been cleared');
        } catch (err) {
          message.error('Failed to reset ontology: ' + (err.message || 'Unknown error'));
        }
      },
    });
  };

  return (
    <div className="page-container">
      <Title level={2} style={{ color: 'var(--theme-primary)' }}>Data Ontology</Title>
      <Paragraph style={{ fontSize: '15px', color: '#8c8c8c', marginBottom: '32px' }}>
        Interactive visualization of data entity relationships and dependencies.
        Explore how tables and views connect in your database.
      </Paragraph>

      {/* Statistics */}
      {!loading && mermaidCode && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Total Views" 
                value={stats.viewCount} 
                prefix={<NodeIndexOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Total Relationships" 
                value={stats.relationshipCount}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Filtered Views" 
                value={stats.filteredViewCount}
                valueStyle={{ color: selectedViews.length > 0 ? '#cf1322' : '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Filtered Relationships" 
                value={stats.filteredRelationshipCount}
                valueStyle={{ color: selectedViews.length > 0 ? '#cf1322' : '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filter Controls */}
      <Card 
        title={
          <Space>
            <FilterOutlined />
            <span>Filter Diagram</span>
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Select Views to Focus On:</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: '8px' }}
              placeholder="Select one or more views..."
              value={selectedViews}
              onChange={setSelectedViews}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
                (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={allViews.map(view => ({ 
                label: (viewAccessDenied[view]
                  ? `${viewLabels[view] || view.split('_').pop()}  — No access`
                  : (viewLabels[view] || view.split('_').pop())),
                value: view,
                disabled: !!viewAccessDenied[view],
              }))}
              maxTagCount="responsive"
            />
          </div>
          
          <div>
            <Text strong>Search Mode:</Text>
            <div style={{ marginTop: '8px' }}>
              <Radio.Group value={searchMode} onChange={e => setSearchMode(e.target.value)}>
                <Radio.Button value="connected_paths">
                  <BranchesOutlined /> Connected Paths
                </Radio.Button>
                <Radio.Button value="relationship_levels">
                  <ApartmentOutlined /> Relationship Levels
                </Radio.Button>
              </Radio.Group>
              <Text type="secondary" style={{ marginLeft: '12px', fontSize: '12px' }}>
                {searchMode === 'connected_paths'
                  ? '(Shows selected views + intermediate views on shortest connecting paths)'
                  : '(Shows all views within N hops from selected views)'}
              </Text>
            </div>
          </div>

          {searchMode === 'relationship_levels' && (
          <div>
            <Text strong>Relationship Levels:</Text>
            <InputNumber
              min={1}
              max={10}
              value={relationshipLevels}
              onChange={setRelationshipLevels}
              style={{ marginLeft: '12px' }}
              addonAfter="levels"
            />
            <Text type="secondary" style={{ marginLeft: '12px', fontSize: '12px' }}>
              (Number of connection hops from selected views)
            </Text>
          </div>
          )}

          <Space>
            <Button 
              type="primary" 
              icon={<FilterOutlined />}
              onClick={handleApplyFilter}
              disabled={!selectedViews || selectedViews.length === 0}
            >
              Apply Filter
            </Button>
            <Button 
              icon={<ClearOutlined />}
              onClick={handleResetFilter}
              disabled={selectedViews.length === 0}
            >
              Reset Filter
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={loadMermaidDiagram}
              loading={loading}
            >
              Reload Diagram
            </Button>
            <Tooltip title="Delete the generated ontology file. The ontology will be rebuilt automatically on the next catalog sync (Data Explorer ▸ Sync now, or the scheduled job).">
              <Button
                icon={<DeleteOutlined />}
                onClick={handleResetOntology}
                disabled={!mermaidCode}
                danger
              >
                Reset Ontology
              </Button>
            </Tooltip>
          </Space>

          {selectedViews.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <Tag color="blue">
                Active Filter: {selectedViews.length} view(s) — {searchMode === 'connected_paths' ? 'Connected Paths' : `${relationshipLevels} level(s)`}
              </Tag>
            </div>
          )}

          {ontologySource && (
            <Alert
              type="info"
              showIcon
              icon={<DatabaseOutlined />}
              message={
                <span>
                  Ontology generated dynamically from <Tag color={ontologySource === 'cache' ? 'green' : 'blue'}>{ontologySource}</Tag> catalog data.
                  Relationships extracted from <code>LINK_TO()</code> patterns in column descriptions.
                </span>
              }
              style={{ marginTop: '8px' }}
              closable
              onClose={() => setOntologySource(null)}
            />
          )}
        </Space>
      </Card>

      {/* Mermaid Diagram */}
      {loading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '100px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading ontology diagram...</div>
          </div>
        </Card>
      ) : mermaidCode ? (
        <Card 
          title={
            <Space>
              <NodeIndexOutlined />
              <span>Ontology Diagram</span>
            </Space>
          }
        >
          <MermaidViewer code={filteredMermaidCode} height={700} />
        </Card>
      ) : (
        <Card>
          <Empty
            description="No ontology diagram available. The ontology is now rebuilt automatically after every Data Catalog Sync. Go to Data Explorer and trigger a sync (or wait for the scheduled job) to populate it."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}
    </div>
  );
}

export default Ontology;
